import { useMemo, useState } from 'react';
import {
    StatusChip, TimelineButton, LegalTimelineModal,
    STATUS_FIELD_LABELS, STATUS_FIELD_ORDER, daysBetweenIso, fmtDate as fmtIsoDate,
} from './legalShared';

const EMPTY_ARRAY = [];

// Terminal states — a document here is "done" (one way or another), so it
// stops aging. Everything else (In Progress, On Hold, Signed copy awaited,
// blank) is still open and counts toward the aging view.
const CLOSED_STATUSES = new Set(['Completed', 'Cancelled']);

const BUCKETS = [
    { key: '0-7', label: '0–7 Days', min: 0, max: 7, color: '#059669' },
    { key: '8-15', label: '8–15 Days', min: 8, max: 15, color: '#0891b2' },
    { key: '16-30', label: '16–30 Days', min: 16, max: 30, color: '#d97706' },
    { key: '31-60', label: '31–60 Days', min: 31, max: 60, color: '#ea580c' },
    { key: '61-90', label: '61–90 Days', min: 61, max: 90, color: '#dc2626' },
    { key: '90+', label: '90+ Days', min: 91, max: Infinity, color: '#991b1b' },
];

function bucketFor(days) {
    return BUCKETS.find(b => days >= b.min && days <= b.max) || BUCKETS[BUCKETS.length - 1];
}

function daysAgeColor(days) {
    return bucketFor(days).color;
}

// create_date is only populated for manually created/edited records — bulk
// imports leave it blank (see legal_excel.py's HEADER_TO_FIELD, which has no
// mapping for it), so fall back to when the workbook itself was imported.
function effectiveCreated(record, importedAt) {
    const raw = record.create_date || importedAt;
    if (!raw) return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function daysBetween(from, to) {
    return Math.max(0, Math.floor((to - from) / 86400000));
}

// Bifurcates a document's total age into per-field stages, using each
// status field's latest effective date (row.status_effective_dates, added
// by legal_views/shared.py's _record_json) as a proxy stage-start marker.
// Ordering the six fields by when they were last effective-dated and
// diffing consecutive dates answers "how long did each team actually hold
// this" instead of blaming whichever team has it today for the full age —
// the exact business complaint this feature was built to fix. For the
// precise, fully-editable history use the 🕘 Timeline popup.
function stageBreakdown(row, todayIso) {
    const dates = row.status_effective_dates || {};
    const entries = STATUS_FIELD_ORDER
        .map(field => ({ field, date: dates[field] }))
        .filter(e => e.date)
        .sort((a, b) => a.date.localeCompare(b.date));
    return entries.map((e, i) => {
        const next = entries[i + 1];
        const end = next ? next.date : todayIso;
        return {
            field: e.field,
            label: STATUS_FIELD_LABELS[e.field],
            from: e.date,
            days: daysBetweenIso(e.date, end),
            isCurrent: !next,
        };
    });
}

function StageBreakdownRow({ row, todayIso }) {
    const stages = stageBreakdown(row, todayIso);
    if (!stages.length) return null;
    return (
        <div className="legal-aging-breakdown">
            {stages.map(s => (
                <span key={s.field} className={`legal-aging-breakdown-chip${s.isCurrent ? ' legal-aging-breakdown-current' : ''}`} title={`${s.label} since ${fmtIsoDate(s.from)}`}>
                    {s.label}: {s.days}d{s.isCurrent ? ' (current)' : ''}
                </span>
            ))}
        </div>
    );
}

function BucketBar({ bucket, count, max, onClick }) {
    const pct = max ? Math.max(count ? 4 : 0, (count / max) * 100) : 0;
    return (
        <button
            type="button"
            className="legal-aging-bucket-row"
            onClick={onClick}
            style={{ '--bucket-color': bucket.color }}
        >
            <span className="legal-aging-bucket-label">{bucket.label}</span>
            <span className="legal-aging-bucket-track">
                <span className="legal-aging-bucket-fill" style={{ width: `${pct}%`, background: bucket.color }} />
            </span>
            <span className="legal-aging-bucket-count">{count}</span>
        </button>
    );
}

function fmtDate(date) {
    return date ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
}

// Same visual pattern as LegalInsights.js's LegalDrillModal (title/sub/count
// badge/close + a document table), so clicking through from the Aging tab
// feels consistent with the rest of the dashboard.
function AgingDrillModal({ title, sub, rows, todayIso, onTimeline, onClose }) {
    const single = rows.length === 1 ? rows[0] : null;
    return (
        <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="legal-modal" style={{ maxWidth: 860 }}>
                <div className="legal-modal-hdr">
                    <div className="legal-modal-icon">⏱️</div>
                    <div>
                        <div className="legal-modal-title">{title}</div>
                        <div className="legal-modal-sub">{sub}</div>
                    </div>
                    <span className="legal-drill-count">{rows.length}</span>
                    <button className="legal-modal-close" onClick={onClose} style={{ marginLeft: 12 }}>✕</button>
                </div>
                <div className="legal-modal-body" style={{ padding: 0 }}>
                    {rows.length ? (
                        <div className="legal-drill-table-wrap">
                            <table className="legal-drill-table">
                                <thead>
                                    <tr>
                                        <th>Entity Name</th>
                                        <th>Document Category</th>
                                        <th>Document Sub-Category</th>
                                        <th>Requestor</th>
                                        <th>Country</th>
                                        <th>Current Status</th>
                                        <th>Initiated On</th>
                                        <th>Days Open</th>
                                        <th>Timeline</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(row => (
                                        <tr key={row.id}>
                                            <td><strong>{row.entity_name || '—'}</strong></td>
                                            <td>{row.document_category || '—'}</td>
                                            <td>{row.document_subcategory || '—'}</td>
                                            <td>{row.requestor_name || '—'}</td>
                                            <td>{row.country || '—'}</td>
                                            <td><StatusChip value={row.current_status} /></td>
                                            <td>{fmtDate(row.initiatedOn)}</td>
                                            <td>
                                                <span className="legal-aging-days-badge" style={{ color: daysAgeColor(row.daysOpen), borderColor: daysAgeColor(row.daysOpen) }}>
                                                    {row.daysOpen}d
                                                </span>
                                            </td>
                                            <td><TimelineButton onClick={() => onTimeline(row)} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="legal-drill-empty">No open documents in this range.</div>
                    )}
                    {single && (
                        <div style={{ padding: '12px 18px 16px' }}>
                            <div className="legal-ic-sub" style={{ marginBottom: 6 }}>Aging bifurcation — how long each stage has actually held this document</div>
                            <StageBreakdownRow row={single} todayIso={todayIso} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function LegalAging({ data, canManageData, onStatusChanged }) {
    const [search, setSearch] = useState('');
    const [drill, setDrill] = useState(null);
    const [timelineRecord, setTimelineRecord] = useState(null);

    const importedAt = data?.import?.imported_at || null;
    const records = data?.records || EMPTY_ARRAY;
    const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

    const openRows = useMemo(() => {
        const today = new Date();
        return records
            .filter(row => !CLOSED_STATUSES.has(row.current_status))
            .map(row => {
                const created = effectiveCreated(row, importedAt);
                const days = created ? daysBetween(created, today) : null;
                return { ...row, initiatedOn: created, daysOpen: days };
            })
            .filter(row => row.daysOpen !== null)
            .sort((a, b) => b.daysOpen - a.daysOpen);
    }, [records, importedAt]);

    const bucketCounts = useMemo(() => {
        const counts = Object.fromEntries(BUCKETS.map(b => [b.key, 0]));
        openRows.forEach(row => { counts[bucketFor(row.daysOpen).key] += 1; });
        return counts;
    }, [openRows]);

    const kpis = useMemo(() => {
        const total = openRows.length;
        const totalDays = openRows.reduce((sum, row) => sum + row.daysOpen, 0);
        const oldest = openRows.reduce((max, row) => Math.max(max, row.daysOpen), 0);
        const overdue = openRows.filter(row => row.daysOpen > 30).length;
        return {
            total,
            avg: total ? Math.round(totalDays / total) : 0,
            oldest,
            overdue,
        };
    }, [openRows]);

    const visibleRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return openRows;
        return openRows.filter(row =>
            (row.entity_name || '').toLowerCase().includes(q)
            || (row.document_category || '').toLowerCase().includes(q)
            || (row.document_subcategory || '').toLowerCase().includes(q)
            || (row.requestor_name || '').toLowerCase().includes(q)
        );
    }, [openRows, search]);

    const maxBucketCount = Math.max(...BUCKETS.map(b => bucketCounts[b.key]), 1);

    const openBucketDrill = (bucket) => {
        const rows = openRows.filter(row => bucketFor(row.daysOpen).key === bucket.key);
        setDrill({
            title: `Aging: ${bucket.label}`,
            sub: `${rows.length} open document${rows.length === 1 ? '' : 's'} initiated ${bucket.label.toLowerCase()} ago`,
            rows,
        });
    };

    return (
        <div className="legal-ov">
            <div className="legal-hero-row">
                <div className="legal-hero-tile legal-hero-slate">
                    <span className="legal-hero-icon">⏳</span>
                    <div className="legal-hero-body">
                        <div className="legal-hero-label">Open Documents</div>
                        <div className="legal-hero-val">{kpis.total}</div>
                        <div className="legal-hero-sub">initiated, not yet completed</div>
                    </div>
                </div>
                <div className="legal-hero-tile legal-hero-cyan">
                    <span className="legal-hero-icon">📈</span>
                    <div className="legal-hero-body">
                        <div className="legal-hero-label">Avg Days Open</div>
                        <div className="legal-hero-val">{kpis.avg}</div>
                        <div className="legal-hero-sub">days since initiated</div>
                    </div>
                </div>
                <div className="legal-hero-tile legal-hero-amber">
                    <span className="legal-hero-icon">🕰️</span>
                    <div className="legal-hero-body">
                        <div className="legal-hero-label">Oldest Open</div>
                        <div className="legal-hero-val">{kpis.oldest}</div>
                        <div className="legal-hero-sub">days, longest-waiting document</div>
                    </div>
                </div>
                <div className="legal-hero-tile legal-hero-red">
                    <span className="legal-hero-icon">🚨</span>
                    <div className="legal-hero-body">
                        <div className="legal-hero-label">Overdue (30+ Days)</div>
                        <div className="legal-hero-val">{kpis.overdue}</div>
                        <div className="legal-hero-sub">{kpis.total ? Math.round((kpis.overdue / kpis.total) * 100) : 0}% of open documents</div>
                    </div>
                </div>
            </div>

            <div className="legal-ic">
                <div className="legal-ic-title">⏱️ Aging Breakdown</div>
                <div className="legal-ic-sub">Click a bucket to see its documents</div>
                <div className="legal-aging-buckets">
                    {BUCKETS.map(bucket => (
                        <BucketBar
                            key={bucket.key}
                            bucket={bucket}
                            count={bucketCounts[bucket.key]}
                            max={maxBucketCount}
                            onClick={() => openBucketDrill(bucket)}
                        />
                    ))}
                </div>
            </div>

            <div className="legal-ic">
                <div className="legal-ic-title">📋 Open Documents</div>
                <div className="legal-ic-sub">Sorted oldest first · {visibleRows.length} of {openRows.length} shown · click a row to see it in detail</div>
                <input
                    className="legal-search"
                    style={{ marginBottom: 10, width: '100%', maxWidth: 320 }}
                    placeholder="Search entity, category, requestor…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <div className="legal-drill-table-wrap">
                    <table className="legal-drill-table">
                        <thead>
                            <tr>
                                <th>Entity Name</th>
                                <th>Document Category</th>
                                <th>Document Sub-Category</th>
                                <th>Requestor</th>
                                <th>Current Status</th>
                                <th>Initiated On</th>
                                <th>Days Open</th>
                                <th>Timeline</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map(row => (
                                <tr
                                    key={row.id}
                                    className="legal-aging-row-clickable"
                                    onClick={() => setDrill({
                                        title: row.entity_name || 'Document',
                                        sub: `${row.document_category || 'Unspecified category'} · requested by ${row.requestor_name || 'Unknown'}`,
                                        rows: [row],
                                    })}
                                >
                                    <td><strong>{row.entity_name || '—'}</strong></td>
                                    <td>{row.document_category || '—'}</td>
                                    <td>{row.document_subcategory || '—'}</td>
                                    <td>{row.requestor_name || '—'}</td>
                                    <td><StatusChip value={row.current_status} /></td>
                                    <td>{fmtDate(row.initiatedOn)}</td>
                                    <td>
                                        <span className="legal-aging-days-badge" style={{ color: daysAgeColor(row.daysOpen), borderColor: daysAgeColor(row.daysOpen) }}>
                                            {row.daysOpen}d
                                        </span>
                                    </td>
                                    <td onClick={e => e.stopPropagation()}><TimelineButton onClick={() => setTimelineRecord(row)} /></td>
                                </tr>
                            ))}
                            {!visibleRows.length && (
                                <tr><td colSpan={7} className="legal-drill-empty">No open documents match this view.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {drill && (
                <AgingDrillModal
                    {...drill}
                    todayIso={todayIso}
                    onTimeline={setTimelineRecord}
                    onClose={() => setDrill(null)}
                />
            )}
            {timelineRecord && (
                <LegalTimelineModal
                    recordId={timelineRecord.id}
                    entityName={timelineRecord.entity_name}
                    canManageData={canManageData}
                    onClose={() => setTimelineRecord(null)}
                    onChanged={onStatusChanged}
                />
            )}
        </div>
    );
}

export { LegalAging };
export default LegalAging;
